import styled, { useTheme } from "styled-components";
import Card from "common/Boxes/Card";
import React, { useContext } from "react";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { OrderTypes } from "@shared/features/trades/common/types";
import TradingTerminalChart from "features/tradingTerminal/common/TradingTerminalChart";
import { TradingTerminalContext } from "@shared/features/tradingTerminal/TradingTerminal.context";
import ConfirmAndShowErrors from "features/tradingTerminal/Summary/ConfirmAndShowErrors";
import { CreateTradeSections } from "@shared/features/tradingTerminal/common/types";
import { ORDER_TYPE_TO_CREATE_TRADE_TAB } from "@shared/features/tradingTerminal/common/constants";
import Space from "antd/es/space";
import { typography } from "@shared/common/Themes/typography";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import Title from "common/Typography/Title";
import { useGetTooltipConfigForField } from "features/configuration/GeneralConfiguration/ConfigurationItem/ConfigurationItemTooltipsConfig";
import TrailingSection from "features/tradingTerminal/Trailing/TrailingSection";
import { WebTradingTerminalContext } from "features/tradingTerminal/WebTradingTerminal.context";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { LanguageNameSpaces } from "@shared/types";
import { TranslationKeys } from "@shared/common/i18n/types.ts";

const OldTradingTerminal = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADING_TERMINAL);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const appTheme = useTheme();
  const { createTradeLoading, isLoading } = useContext(TradingTerminalContext)!;
  const { sectionToComponent } = useContext(WebTradingTerminalContext)!;
  const { getHelperTooltipProps } = useGetTooltipConfigForField();
  const tooltipProps = getHelperTooltipProps("Trailing");

  const ordersComponents = Object.entries(orderTypeToArea).map(
    ([orderType, area]) => {
      return (
        <StyledCard $area={area} key={area}>
          {sectionToComponent[ORDER_TYPE_TO_CREATE_TRADE_TAB[orderType]]}
        </StyledCard>
      );
    }
  );

  const trailing = (
    <LoadingSpinnerOverlay
      isLoading={isLoading}
      wrap={true}
      hideContentWhileLoading={true}
      backgroundColor={appTheme.colors.cardBgColor}
    >
      <Title
        level={3}
        $weight={typography.fontWeights.SEMI_BOLD}
        style={{ display: "flex", padding: "8px 0px 16px" }}
      >
        {t("sections.trailing")}
        {tooltipProps && (
          <HelperTooltip
            {...tooltipProps}
            description={tCommon(tooltipProps.description)}
            title={tCommon(tooltipProps.title)}
          />
        )}
      </Title>
      <Space size={16} orientation="vertical" style={{ width: "100%" }}>
        {Object.values(OrderTypes).map((orderType) => (
          <TrailingSection orderType={orderType} key={orderType} />
        ))}
      </Space>
    </LoadingSpinnerOverlay>
  );

  return (
    <>
      <ContentWrapper>
        <LoadingSpinnerOverlay
          isLoading={createTradeLoading}
          backgroundColor={appTheme.colors.cardBgColor}
          spinnerSize={"large"}
          zIndex={20}
        >
          <StyledCard $area={Areas.GRAPH} style={{ padding: 8 }}>
            <TradingTerminalChart isCard={true} />
          </StyledCard>
          {ordersComponents}
          <StyledCard $area={Areas.GENERAL} style={{ overflow: "visible" }}>
            {sectionToComponent[CreateTradeSections.GENERAL]}
          </StyledCard>
          <StyledCard $area={Areas.TRAILING} style={{ overflow: "visible" }}>
            {trailing}
          </StyledCard>
          {!isLoading && <ConfirmAndShowErrors />}
        </LoadingSpinnerOverlay>
      </ContentWrapper>
      <div style={{ height: 40 }} />
    </>
  );
};

export default OldTradingTerminal;

enum Areas {
  GENERAL = "general",
  ENTRIES = "entries",
  TAKE_PROFITS = "takeProfits",
  STOP = "stop",
  TRAILING = "trailing",
  GRAPH = "graph",
}

const orderTypeToArea: Record<OrderTypes, Areas> = {
  [OrderTypes.ENTRY]: Areas.ENTRIES,
  [OrderTypes.TAKE_PROFIT]: Areas.TAKE_PROFITS,
  [OrderTypes.STOP]: Areas.STOP,
};

const ROW_HEIGHT = "minmax(auto, 1fr)";
const ContentWrapper = styled.div`
  min-height: inherit;
  height: 100%;
  width: 100%;
  display: grid;
  padding: 16px;
  column-gap: 16px;
  row-gap: 16px;
  
  grid:
      "${Areas.GENERAL} ${Areas.GRAPH} ${Areas.GRAPH}" 400px
      "${Areas.ENTRIES} ${Areas.TAKE_PROFITS} ${Areas.STOP}" ${ROW_HEIGHT}
      "${Areas.ENTRIES} ${Areas.TAKE_PROFITS} ${Areas.TRAILING}" ${ROW_HEIGHT}
      / minmax(470px, 1fr) 1fr 1fr;
  }

  @media (max-width: 1450px) {
    grid:
      "${Areas.GENERAL} ${Areas.GRAPH}" 400px
      "${Areas.ENTRIES} ${Areas.TAKE_PROFITS}" ${ROW_HEIGHT}
      "${Areas.STOP}  ${Areas.TRAILING}" minmax(200px, auto)
      / minmax(470px, 1fr) 1fr;
  }
`;
type CardProps = { $area: Areas };
const StyledCard = styled(Card).attrs<CardProps>((p) => ({
  id: `trade-terminal-${p.$area}`,
}))<CardProps>`
  grid-area: ${(p) => p.$area};
  overflow: hidden;
`;
